import { Tree, Spin } from 'antd';
import React, { useEffect, useState, useRef } from 'react';
import { AleoTypography } from '@aleohq/components';
import { FileOutlined } from '@ant-design/icons';

const transcriptBaseUrl = '';
const transcriptDownloadBaseUrl = transcriptBaseUrl;

interface TreeNode {
    title: string;
    key: string;
    isFile?: boolean;
    isLeaf?: boolean;
    children?: TreeNode[];
}

function getChildren(lines: string[], prefix: string): TreeNode[] {
    // binary search for first occurrence
    let l = 0, r = lines.length - 1;
    let firstIdx = -1;
    while (l <= r) {
        let m = Math.floor((l + r) / 2);
        if (lines[m] >= prefix) {
            firstIdx = m;
            r = m - 1;
        } else {
            l = m + 1;
        }
    }

    if (firstIdx === -1 || !lines[firstIdx].startsWith(prefix)) {
        return [];
    }

    const childrenMap = new Map<string, boolean>();
    const prefixLen = prefix.length;
    
    for (let i = firstIdx; i < lines.length; i++) {
        const line = lines[i];
        if (!line.startsWith(prefix)) break;

        const remainder = line.substring(prefixLen);
        const slashIdx = remainder.indexOf('/');

        if (slashIdx === -1) {
            if (remainder) {
                childrenMap.set(remainder, true); // true = isFile
            }
        } else {
            const folderName = remainder.substring(0, slashIdx);
            if (!childrenMap.has(folderName)) {
                childrenMap.set(folderName, false);
            }
        }
    }

    return Array.from(childrenMap.entries()).map(([name, isFile]) => {
        const fullPath = prefix + name;
        return {
            title: name,
            key: fullPath,
            isLeaf: isFile,
            isFile: isFile,
            icon: isFile ? <FileOutlined /> : null
        };
    });
}

const updateTreeData = (list: TreeNode[], key: React.Key, children: TreeNode[]): TreeNode[] => {
    return list.map((node) => {
        if (node.key === key) {
            return { ...node, children };
        }
        if (node.children) {
            return { ...node, children: updateTreeData(node.children, key, children) };
        }
        return node;
    });
};

const Transcripts: React.FC = () => {
    const linesRef = useRef<string[]>([]);
    const [treeData, setTreeData] = useState<TreeNode[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        fetch(`${transcriptBaseUrl}/transcripts_meta.json`)
          .then((response) => response.json())
          .then((meta) => {
              const fetchPromises = [];
              for (let i = 1; i <= meta.count; i++) {
                  fetchPromises.push(fetch(`${transcriptBaseUrl}/transcripts_part_${i}.txt`).then(res => res.text()));
              }
              return Promise.all(fetchPromises);
          })
          .then((dataParts) => {
              const allData = dataParts.join('\n');
              const fileArray = allData.split('\n').filter((file) => file.trim() !== '');
              linesRef.current = fileArray;
              
              // Load the root level right away
              const rootNodes = getChildren(fileArray, '');
              setTreeData(rootNodes);
              setLoading(false);
          })
          .catch((error) => {
              console.error('Error fetching the file list:', error);
              setLoading(false);
          });
    }, []);

    const onLoadData = (node: any) => {
        return new Promise<void>((resolve) => {
            if (node.children) {
                resolve();
                return;
            }
            
            // Generate children lazily from the flat sorted string array
            const childrenNodes = getChildren(linesRef.current, node.key + '/');
            
            setTreeData((origin) => updateTreeData(origin, node.key, childrenNodes));
            resolve();
        });
    };

    const onDownload = (key: string, isFile: boolean) => {
        if (isFile) {
            const link = document.createElement('a');
            link.href = `${transcriptDownloadBaseUrl}/${key}`;
            link.setAttribute('download', '');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const onSelect = (selectedKeys: React.Key[], info: any) => {
        if (info.selectedNodes[0]) {
            const { key, isFile } = info.selectedNodes[0];
            onDownload(key, !!isFile);
        }
    };

    const containerStyle: React.CSSProperties = {
        border: '1px solid #272935',
        borderRadius: '20px',
        padding: '20px',
        marginTop: '20px',
        backgroundColor: '#1E2029'
    };

    return (
      <>
          <AleoTypography textStyle="title" weight="semibold">
              Transcripts
          </AleoTypography>
          <AleoTypography textStyle="body" style={{ maxWidth: 550 }}>
              Files may take a second to load. Click on a file to download it.
          </AleoTypography>
          <div style={containerStyle}>
              {loading ? (
                <Spin style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }} />
              ) : (
                <Tree
                  showLine={{ showLeafIcon: false }}
                  showIcon={true}
                  loadData={onLoadData}
                  treeData={treeData}
                  onSelect={onSelect}
                  height={window.innerHeight / 2}
                />
              )}
          </div>
      </>
    );
};

export default Transcripts;